import { useState, useRef, useEffect, useCallback } from "react";
import { X, Minus, Square, Maximize2, GripHorizontal } from "lucide-react";

/**
 * DraggableResizableWindow - A reusable wrapper for making any content draggable and resizable
 * 
 * Props:
 * - title: Window title
 * - children: Content to render inside
 * - onClose: Function to call when close button clicked
 * - initialPosition: { x, y } initial position
 * - initialSize: { width, height } initial size
 * - minSize: { width, height } minimum size constraints
 * - maxSize: { width, height } maximum size constraints
 * - zIndex: z-index value
 * - titleBarColor: Custom title bar color class
 * - showMinimize: Show minimize button
 * - showMaximize: Show maximize button
 * - onMinimize: Function to call when minimize clicked
 * - className: Additional classes for the window
 */
const DraggableResizableWindow = ({
  title,
  children,
  onClose,
  initialPosition = { x: 100, y: 100 },
  initialSize = { width: 400, height: 300 },
  minSize = { width: 200, height: 150 },
  maxSize = { width: 1200, height: 800 },
  zIndex = 9990,
  titleBarColor = "bg-gradient-to-r from-[#000080] to-[#1084d0]",
  showMinimize = false,
  showMaximize = true,
  onMinimize,
  className = "",
  resizable = true,
  headerExtra = null,
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const [preMaximizeState, setPreMaximizeState] = useState(null);
  
  const windowRef = useRef(null);
  const sizeRef = useRef(initialSize);
  sizeRef.current = size;

  // Keep the window inside the viewport on mount, on window resize, and on device rotation.
  // This guarantees the title bar is always reachable (fixes off-screen / stuck windows on
  // phones and when turning the phone sideways).
  useEffect(() => {
    const clamp = () => {
      if (isMaximized) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = Math.min(sizeRef.current.width, vw);
      const h = Math.min(sizeRef.current.height, Math.max(140, vh - 40));
      setSize({ width: w, height: h });
      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, Math.max(0, vw - w))),
        y: Math.max(0, Math.min(prev.y, Math.max(0, vh - 40))), // keep the title bar on screen
      }));
    };
    clamp();
    window.addEventListener("resize", clamp);
    window.addEventListener("orientationchange", clamp);
    return () => {
      window.removeEventListener("resize", clamp);
      window.removeEventListener("orientationchange", clamp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaximized]);

  // Handle dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag') || e.target.closest('.resize-handle')) return;
    if (isMaximized) return;
    
    setIsDragging(true);
    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && !isMaximized) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep within viewport
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - size.height;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
    
    if (isResizing && !isMaximized) {
      const rect = windowRef.current.getBoundingClientRect();
      let newWidth = size.width;
      let newHeight = size.height;
      let newX = position.x;
      let newY = position.y;
      
      if (resizeDirection.includes('e')) {
        newWidth = Math.max(minSize.width, Math.min(maxSize.width, e.clientX - rect.left));
      }
      if (resizeDirection.includes('w')) {
        const diff = position.x - (e.clientX - dragOffset.x);
        newWidth = Math.max(minSize.width, Math.min(maxSize.width, size.width + diff));
        if (newWidth !== size.width) {
          newX = e.clientX - dragOffset.x;
        }
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(minSize.height, Math.min(maxSize.height, e.clientY - rect.top));
      }
      if (resizeDirection.includes('n')) {
        const diff = position.y - (e.clientY - dragOffset.y);
        newHeight = Math.max(minSize.height, Math.min(maxSize.height, size.height + diff));
        if (newHeight !== size.height) {
          newY = e.clientY - dragOffset.y;
        }
      }
      
      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, isResizing, dragOffset, size, position, minSize, maxSize, resizeDirection, isMaximized]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Handle resize start
  const startResize = (direction, e) => {
    if (!resizable || isMaximized) return;
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  // Handle maximize
  const toggleMaximize = () => {
    if (isMaximized) {
      // Restore
      if (preMaximizeState) {
        setPosition(preMaximizeState.position);
        setSize(preMaximizeState.size);
      }
      setIsMaximized(false);
    } else {
      // Maximize
      setPreMaximizeState({ position, size });
      setPosition({ x: 0, y: 0 });
      setSize({ width: window.innerWidth, height: window.innerHeight - 40 });
      setIsMaximized(true);
    }
  };

  // Resize handles
  const resizeHandles = resizable && !isMaximized ? (
    <>
      {/* Corners */}
      <div 
        className="resize-handle absolute top-0 left-0 w-3 h-3 cursor-nw-resize" 
        onMouseDown={(e) => startResize('nw', e)}
      />
      <div 
        className="resize-handle absolute top-0 right-0 w-3 h-3 cursor-ne-resize" 
        onMouseDown={(e) => startResize('ne', e)}
      />
      <div 
        className="resize-handle absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize" 
        onMouseDown={(e) => startResize('sw', e)}
      />
      <div 
        className="resize-handle absolute bottom-0 right-0 w-3 h-3 cursor-se-resize" 
        onMouseDown={(e) => startResize('se', e)}
      />
      {/* Edges */}
      <div 
        className="resize-handle absolute top-0 left-3 right-3 h-1 cursor-n-resize" 
        onMouseDown={(e) => startResize('n', e)}
      />
      <div 
        className="resize-handle absolute bottom-0 left-3 right-3 h-1 cursor-s-resize" 
        onMouseDown={(e) => startResize('s', e)}
      />
      <div 
        className="resize-handle absolute left-0 top-3 bottom-3 w-1 cursor-w-resize" 
        onMouseDown={(e) => startResize('w', e)}
      />
      <div 
        className="resize-handle absolute right-0 top-3 bottom-3 w-1 cursor-e-resize" 
        onMouseDown={(e) => startResize('e', e)}
      />
    </>
  ) : null;

  return (
    <div
      ref={windowRef}
      className={`fixed shadow-2xl flex flex-col ${className}`}
      style={{
        left: isMaximized ? 0 : position.x,
        top: isMaximized ? 0 : position.y,
        width: isMaximized ? '100vw' : size.width,
        height: isMaximized ? 'calc(100vh - 40px)' : size.height,
        zIndex,
        border: '2px solid #808080',
        borderTopColor: '#dfdfdf',
        borderLeftColor: '#dfdfdf',
      }}
      data-testid="draggable-window"
    >
      {/* Title Bar */}
      <div 
        className={`${titleBarColor} text-white px-2 py-1 flex items-center justify-between cursor-move select-none shrink-0`}
        onMouseDown={handleMouseDown}
        onDoubleClick={showMaximize ? toggleMaximize : undefined}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <GripHorizontal className="w-4 h-4 text-white/50 shrink-0" />
          <span className="font-bold text-sm truncate">{title}</span>
          {headerExtra}
        </div>
        <div className="flex items-center gap-1 no-drag shrink-0">
          {showMinimize && onMinimize && (
            <button
              className="w-5 h-5 flex items-center justify-center bg-gray-300 hover:bg-gray-400 text-black rounded-sm"
              onClick={onMinimize}
              title="Minimize"
            >
              <Minus className="w-3 h-3" />
            </button>
          )}
          {showMaximize && (
            <button
              className="w-5 h-5 flex items-center justify-center bg-gray-300 hover:bg-gray-400 text-black rounded-sm"
              onClick={toggleMaximize}
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? <Square className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            </button>
          )}
          {onClose && (
            <button
              className="w-5 h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-sm"
              onClick={onClose}
              title="Close"
              data-testid="draggable-window-close-btn"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-[#c0c0c0]">
        {children}
      </div>

      {/* Resize Handles */}
      {resizeHandles}
      
      {/* Resize indicator in corner */}
      {resizable && !isMaximized && (
        <div className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-50">
          <svg viewBox="0 0 16 16" className="w-full h-full">
            <path d="M14 14H12V12H14V14ZM14 10H12V8H14V10ZM10 14H8V12H10V14Z" fill="#808080" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default DraggableResizableWindow;
